import { useCallback, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  addToast,
  Button,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
} from '@structural-climbing/ui'
import { MemberProfileModal } from '../members/MemberProfileModal'
import type { DeskPaymentMethod } from '../invoices/paymentMethod'
import { CartActionError, useCarts } from './useCarts'
import { CartPanel } from './CartPanel'
import { CartStrip } from './CartStrip'
import { OpenCartModal } from './OpenCartModal'
import { ProductGrid } from './ProductGrid'
import { cartCount, cartName, cartTotal, formatMoney } from './money'
import type { Product, ProductCategory } from './types'

const CART_PARAM = 'cart'

interface Props {
  categories: ProductCategory[]
  products: Product[]
  productsById: Map<string, Product>
  currency: string
  /** Stock changed: a sale just went through. */
  onStockChanged: () => void
  /** Shown instead of the grid when there is nothing to sell yet. */
  emptyState: React.ReactNode
}

export function SellTab({
  categories,
  products,
  productsById,
  currency,
  onStockChanged,
  emptyState,
}: Props) {
  const { t } = useTranslation()
  const { carts, loading, openCart, setQuantity, discardCart, sellCart, assignCart } = useCarts()

  // The cart in view lives in the URL, so a reload (or pull-to-refresh in the
  // installed app) comes back to the same climber, not to an empty screen.
  const [searchParams, setSearchParams] = useSearchParams()
  const activeCart = carts.find((cart) => cart.id === searchParams.get(CART_PARAM)) ?? null
  const setActiveCartId = useCallback(
    (cartId: string | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          if (cartId) params.set(CART_PARAM, cartId)
          else params.delete(CART_PARAM)
          return params
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const [pickerOpen, setPickerOpen] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  // A quick sale being opened. Taps that land before the server answers are
  // counted here and all go into that one cart, so a fast double tap does not
  // open two.
  const quickSale = useRef<Map<string, number> | null>(null)
  const [mobileCartOpen, setMobileCartOpen] = useState(false)
  const [selling, setSelling] = useState(false)
  const [profileTarget, setProfileTarget] = useState<string | null>(null)

  const reportCartError = useCallback(
    (e: unknown, fallback: string) => {
      const closed = e instanceof CartActionError && e.reason === 'closed'
      addToast({ title: closed ? t('products.errorClosed') : fallback, color: 'danger' })
    },
    [t],
  )

  const changeQuantity = useCallback(
    (cartId: string, productId: string, quantity: number) => {
      setQuantity(cartId, productId, quantity).catch((e: unknown) =>
        reportCartError(e, t('products.errorSave')),
      )
    },
    [setQuantity, reportCartError, t],
  )

  function addProduct(product: Product) {
    if (activeCart) {
      const current = activeCart.items.find((line) => line.product_id === product.id)?.quantity ?? 0
      changeQuantity(activeCart.id, product.id, current + 1)
      return
    }
    // No cart selected: the tap starts a quick sale, with no questions asked.
    if (quickSale.current) {
      quickSale.current.set(product.id, (quickSale.current.get(product.id) ?? 0) + 1)
      return
    }
    const taps = new Map([[product.id, 1]])
    quickSale.current = taps
    openCart({ label: t('products.quickSale') })
      .then((cart) => {
        setActiveCartId(cart.id)
        for (const [productId, count] of taps) changeQuantity(cart.id, productId, count)
      })
      .catch(() => addToast({ title: t('products.errorOpen'), color: 'danger' }))
      .finally(() => {
        quickSale.current = null
      })
  }

  async function handleOpenCart(target: { userId: string } | { label: string }) {
    const cart = await openCart(target)
    setActiveCartId(cart.id)
  }

  async function handleAssign(target: { userId: string } | { label: string }) {
    if (!activeCart || !('userId' in target)) return
    // When the member already had a cart, this is that one, with the lines added.
    const cart = await assignCart(activeCart.id, target.userId)
    setActiveCartId(cart.id)
  }

  async function handleSell(method: DeskPaymentMethod) {
    if (!activeCart) return
    setSelling(true)
    try {
      const total = await sellCart(activeCart.id, method)
      addToast({
        title: t('products.sold', { total: formatMoney(total, currency) }),
        color: 'success',
      })
      setActiveCartId(null)
      setMobileCartOpen(false)
      onStockChanged()
    } catch (e) {
      reportCartError(e, t('products.errorSell'))
    } finally {
      setSelling(false)
    }
  }

  async function handleDiscard() {
    if (!activeCart) return
    try {
      await discardCart(activeCart.id)
      setActiveCartId(null)
      setMobileCartOpen(false)
    } catch (e) {
      reportCartError(e, t('products.errorSave'))
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size="sm" color="primary" />
      </div>
    )
  }

  const hasProducts = products.some((product) => product.is_active)

  const panel = activeCart && (
    <CartPanel
      cart={activeCart}
      productsById={productsById}
      currency={currency}
      selling={selling}
      onSetQuantity={(productId, quantity) => changeQuantity(activeCart.id, productId, quantity)}
      onSell={(method) => void handleSell(method)}
      onDiscard={() => void handleDiscard()}
      onOpenProfile={setProfileTarget}
      onAssign={() => setAssignOpen(true)}
    />
  )

  const strip = (
    <CartStrip
      carts={carts}
      activeCartId={activeCart?.id ?? null}
      productsById={productsById}
      currency={currency}
      onSelect={setActiveCartId}
      onNewSale={() => setActiveCartId(null)}
      onNewMemberCart={() => setPickerOpen(true)}
    />
  )

  return (
    <div className="space-y-4">
      {!hasProducts ? (
        <>
          {strip}
          {emptyState}
        </>
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <ProductGrid
            categories={categories}
            products={products}
            currency={currency}
            activeCart={activeCart}
            onAdd={addProduct}
            toolbar={strip}
          />

          {/* Wide screens keep the cart beside the grid, always in view. */}
          <Card className="sticky top-4 hidden lg:flex">
            <CardBody className="p-4">
              {panel ?? (
                <p className="py-8 text-center text-sm text-foreground/50">
                  {t('products.tapToStart')}
                </p>
              )}
            </CardBody>
          </Card>
        </div>
      )}

      {/* On a phone the grid has the whole screen and the cart is one tap
          away, pinned to the bottom edge where the thumb already is. */}
      {activeCart && (
        <div className="sticky bottom-0 z-20 -mx-4 border-t border-divider bg-background px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden">
          <Button
            size="lg"
            color="primary"
            className="w-full justify-between font-semibold"
            onPress={() => setMobileCartOpen(true)}
          >
            <span className="min-w-0 truncate">
              {cartName(activeCart) || t('products.guest')} ·{' '}
              {t('products.itemCount', { count: cartCount(activeCart) })}
            </span>
            <span className="shrink-0 tabular-nums">
              {formatMoney(cartTotal(activeCart, productsById), currency)}
            </span>
          </Button>
        </div>
      )}

      <Modal
        isOpen={mobileCartOpen && activeCart !== null}
        size="full"
        scrollBehavior="inside"
        className="lg:hidden"
        onOpenChange={(open) => {
          if (!open) setMobileCartOpen(false)
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>{t('products.viewCart')}</ModalHeader>
              <ModalBody className="pb-[max(1.5rem,env(safe-area-inset-bottom))]">
                {panel}
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>

      <OpenCartModal
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onOpen={handleOpenCart}
      />

      <OpenCartModal
        mode="assign"
        isOpen={assignOpen}
        onClose={() => setAssignOpen(false)}
        onOpen={handleAssign}
      />

      {profileTarget && (
        <MemberProfileModal userId={profileTarget} onClose={() => setProfileTarget(null)} />
      )}
    </div>
  )
}
